import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  TICKET_STATUSES,
  ticketDisplaySubject,
  type AgentSummary,
  type Category,
  type Contact,
  type Organization,
  type TicketAttachment,
  type TicketDetail,
  type TicketMessage,
  type TicketStatus,
  type TimeEntry,
  canWriteQuotes,
} from '@t3/shared';
import { AlertTriangle, ChevronDown, ChevronRight, MessageCircle, Trash2 } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card, StatusBadge, TypeBadge } from '../components/Card.tsx';
import { CustomFieldsPanel } from '../components/CustomFieldsPanel.tsx';
import { QuoteScopePanel } from '../components/QuoteScopePanel.tsx';
import { ProjectWorkPanel } from '../components/ProjectWorkPanel.tsx';
import { DeleteCascadeDialog } from '../components/DeleteCascadeDialog.tsx';
import { EmailBody, emailPreview } from '../components/EmailBody.tsx';
import { FormField } from '../components/FormField.tsx';
import { HuduPasswordModal } from '../components/HuduPasswordModal.tsx';
import { Select } from '../components/Select.tsx';
import { Textarea } from '../components/Textarea.tsx';
import { TicketHoursCard } from '../components/TicketHoursCard.tsx';
import { TimeEntryEditDialog } from '../components/TimeEntryEditDialog.tsx';
import { useToast } from '../components/Toaster.tsx';
import { useHiddenStatuses } from '../hooks/useHiddenStatuses.ts';
import { useTimer } from '../timer/useTimer.ts';

interface DetailResponse {
  ticket: TicketDetail;
  messages: TicketMessage[];
  timeEntries: TimeEntry[];
  totalMinutes: number;
  attachments: TicketAttachment[];
}

interface TicketSuggestion {
  nextSteps: string[];
  draftReplyText: string | null;
  escalateToCall: boolean;
  escalationReason: string | null;
  sentiment: 'calm' | 'neutral' | 'frustrated';
}

interface AskMessage {
  role: 'user' | 'assistant';
  content: string;
}

function formatElapsed(startedAt: string): string {
  const ms = Date.now() - new Date(startedAt).getTime();
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

// Self-contained 1-second ticking clock. Keeping the interval here (rather
// than re-rendering the whole ticket page every second) means the message
// thread and description DOM stay untouched while a timer runs — otherwise
// React rewrites their innerHTML each tick and wipes any text the user is
// trying to select/copy.
function LiveElapsed({ startedAt }: { startedAt: string }): React.ReactElement {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  return <>{formatElapsed(startedAt)}</>;
}

interface TicketDeletePreview {
  ticket: { id: string; displayNumber: number; subject: string; organizationName: string };
  counts: {
    messages: number;
    timeEntries: number;
    totalMinutes: number;
    attachments: number;
    csatSurveys: number;
  };
  quoteSourceForProject: string | null;
}

export function TicketDetailPage(): React.ReactElement {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { agent } = useAuth();
  const isAdmin = agent?.role === 'admin';
  // `time_admin` gets the same powers as `admin` for time-entry surfaces
  // only (add/edit/restore on any agent); everything else stays admin-only.
  const isTimeAdmin = isAdmin || agent?.role === 'time_admin';

  const hiddenStatuses = useHiddenStatuses();
  const toast = useToast();

  // Closing a ticket requires requester + category + assigned agent
  // (enforced both client- and server-side). When any are missing we
  // open this modal instead of failing the action — agents can fill in
  // the gaps right there and then complete the close in one step.
  const [closeDialog, setCloseDialog] = useState<{
    targetStatus: TicketStatus;
    needRequester: boolean;
    needCategory: boolean;
    needAgent: boolean;
    requesterId: string;
    categoryId: string;
    agentId: string;
    busy: boolean;
    error: string | null;
  } | null>(null);

  const FINAL_TICKET_STATUSES: readonly TicketStatus[] = ['solved', 'cancelled', 'dead'];

  const [deletePreview, setDeletePreview] = useState<TicketDeletePreview | null>(null);
  const [deletePreviewBusy, setDeletePreviewBusy] = useState(false);
  const [deletePreviewError, setDeletePreviewError] = useState<string | null>(null);

  const [data, setData] = useState<DetailResponse | null>(null);
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editingClientNumber, setEditingClientNumber] = useState(false);
  const [clientNumberDraft, setClientNumberDraft] = useState('');
  const [savingClientNumber, setSavingClientNumber] = useState(false);

  const [reply, setReply] = useState('');
  const [replyPublic, setReplyPublic] = useState(true);
  const [replyCc, setReplyCc] = useState('');
  const [replyError, setReplyError] = useState<string | null>(null);
  const [replyBusy, setReplyBusy] = useState(false);
  const [replyFiles, setReplyFiles] = useState<File[]>([]);

  // IDs of messages the technician has collapsed in the thread. On first
  // load of a ticket, everything except the most recent three messages is
  // collapsed by default — long threads usually only need the latest
  // exchange in view. After that, expand/collapse is per-message and
  // persists across in-thread refreshes (e.g. after posting a reply).
  const [collapsedMessages, setCollapsedMessages] = useState<Set<string>>(new Set());
  const initialCollapseTicketIdRef = useRef<string | null>(null);
  const ccSeededTicketIdRef = useRef<string | null>(null);
  function toggleCollapsed(messageId: string) {
    setCollapsedMessages((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) next.delete(messageId);
      else next.add(messageId);
      return next;
    });
  }

  // Inline two-step confirm for deleting an internal note: first click arms the
  // confirm on that message id, second click deletes. Admin-only.
  const [confirmDeleteMsgId, setConfirmDeleteMsgId] = useState<string | null>(null);
  const [deletingMsgId, setDeletingMsgId] = useState<string | null>(null);

  async function deleteInternalNote(messageId: string) {
    setDeletingMsgId(messageId);
    try {
      await apiFetch(`/api/tickets/${id}/messages/${messageId}`, { method: 'DELETE' });
      setConfirmDeleteMsgId(null);
      await load();
      toast.success('Internal note deleted.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to delete note.');
    } finally {
      setDeletingMsgId(null);
    }
  }

  // The running timer is shared state across the header pill, the popped-out
  // mini window, and this page — useTimer owns it and keeps them in sync.
  const timerCtl = useTimer();
  const activeTimer = timerCtl.timer;
  const timerBusy = timerCtl.busy;
  // Void/restore of committed entries is this page's own concern; timer
  // start/stop errors come from the hook.
  const [entryError, setEntryError] = useState<string | null>(null);
  // A runaway timer can only be resolved where the duration field lives — the
  // timer panel — so the message from here has to point at it rather than
  // leaving the tech with a refusal and no next step.
  const timerError = timerCtl.error
    ? timerCtl.runaway
      ? `${timerCtl.error} Open the timer in the header to enter it.`
      : timerCtl.error
    : entryError;

  const [convertBusy, setConvertBusy] = useState(false);
  const [convertError, setConvertError] = useState<string | null>(null);
  // Lifted out of the quote document panel so the Details panel below can
  // lock the fields the document owns. One fetch, one answer.
  const [hasQuoteDocument, setHasQuoteDocument] = useState(false);

  // Admin time-entry add/edit dialog state. `entry: undefined` => add mode,
  // anything else => edit mode for that entry.
  const [timeDialog, setTimeDialog] = useState<{ entry: TimeEntry | null } | null>(null);
  const [escalationBudget, setEscalationBudget] = useState('');
  const [escalationProjectName, setEscalationProjectName] = useState('');

  const [showHuduPasswords, setShowHuduPasswords] = useState(false);

  const [aiSuggestion, setAiSuggestion] = useState<TicketSuggestion | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiAt, setAiAt] = useState<Date | null>(null);

  // Open-ended "Ask Claude" chat. The conversation lives only in component
  // state — the backend session is stateless and gets the full thread each turn.
  const [askMessages, setAskMessages] = useState<AskMessage[]>([]);
  const [askInput, setAskInput] = useState('');
  const [askBusy, setAskBusy] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const askScrollRef = useRef<HTMLDivElement | null>(null);

  const [showAttachProject, setShowAttachProject] = useState(false);
  const [orgProjects, setOrgProjects] = useState<{ id: string; name: string; status: string }[] | null>(null);
  const [attachProjectId, setAttachProjectId] = useState('');
  const [attachBusy, setAttachBusy] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [orgChangeBusy, setOrgChangeBusy] = useState(false);
  const [requesterError, setRequesterError] = useState<string | null>(null);
  const [orgError, setOrgError] = useState<string | null>(null);

  async function load() {
    try {
      const detail = await apiFetch<DetailResponse>(`/api/tickets/${id}`);
      setData(detail);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Failed to load ticket.');
    }
  }

  useEffect(() => {
    void load();
    setAiSuggestion(null);
    setAiError(null);
    setAiAt(null);
    apiFetch<{ agents: AgentSummary[] }>('/api/agents')
      .then((r) => setAgents(r.agents))
      .catch(() => setAgents([]));
    apiFetch<{ categories: Category[] }>('/api/categories')
      .then((r) => setCategories(r.categories))
      .catch(() => setCategories([]));
    apiFetch<{ organizations: Organization[] }>('/api/organizations?status=all')
      .then((r) => setOrganizations(r.organizations))
      .catch(() => setOrganizations([]));
  }, [id]);

  // First time data arrives for a given ticket id, collapse everything
  // except the latest three messages. Guarded by a ref so subsequent
  // reloads of the same ticket (after posting a reply, etc.) leave the
  // user's expand/collapse choices alone.
  useEffect(() => {
    if (!data) return;
    if (initialCollapseTicketIdRef.current === data.ticket.id) return;
    initialCollapseTicketIdRef.current = data.ticket.id;
    const all = data.messages;
    if (all.length <= 3) {
      setCollapsedMessages(new Set());
      return;
    }
    const toCollapse = all.slice(0, all.length - 3).map((m) => m.id);
    setCollapsedMessages(new Set(toCollapse));
  }, [data]);

  // Seed the reply CC box once per ticket from the most recent public reply
  // that copied anyone, so the same stakeholders stay on the thread without
  // re-typing. Guarded by a ref so reloads (after posting) don't clobber
  // whatever the agent has currently typed.
  useEffect(() => {
    if (!data) return;
    if (ccSeededTicketIdRef.current === data.ticket.id) return;
    ccSeededTicketIdRef.current = data.ticket.id;
    const lastWithCc = [...data.messages]
      .reverse()
      .find((m) => m.isPublic && m.ccEmails.length > 0);
    setReplyCc(lastWithCc ? lastWithCc.ccEmails.join(', ') : '');
  }, [data]);

  useEffect(() => {
    if (!data?.ticket.organizationId) {
      setContacts([]);
      return;
    }
    apiFetch<{ contacts: Contact[] }>(
      `/api/organizations/${encodeURIComponent(data.ticket.organizationId)}/contacts`,
    )
      .then((r) => setContacts(r.contacts))
      .catch(() => setContacts([]));
  }, [data?.ticket.organizationId]);

  function openCloseDialog(targetStatus: TicketStatus, t: TicketDetail) {
    setCloseDialog({
      targetStatus,
      needRequester: !t.requesterContactId,
      needCategory: !t.categoryId,
      needAgent: !t.assignedAgentId,
      requesterId: '',
      categoryId: '',
      agentId: '',
      busy: false,
      error: null,
    });
  }

  async function changeStatus(newStatus: TicketStatus) {
    if (!data) return;
    const t = data.ticket;
    // Closing-a-ticket invariants (mirrors backend): solved / cancelled /
    // dead require requester + category + assigned agent. Instead of
    // erroring, surface a modal that lets the agent fill in the missing
    // pieces and finish the close in one shot — see openCloseDialog +
    // submitCloseDialog.
    if (FINAL_TICKET_STATUSES.includes(newStatus)) {
      if (!t.requesterContactId || !t.categoryId || !t.assignedAgentId) {
        openCloseDialog(newStatus, t);
        return;
      }
    }
    try {
      const res = await apiFetch<{ ticket: TicketDetail }>(`/api/tickets/${id}/status`, {
        method: 'PATCH',
        json: { status: newStatus },
      });
      setData({ ...data, ticket: res.ticket });
    } catch (err) {
      // Safety net: server has stricter rules than the client, or state
      // shifted between render and click. Open the same dialog rather
      // than blowing the page away.
      if (err instanceof ApiError && err.code === 'close_requirements_unmet') {
        openCloseDialog(newStatus, t);
        return;
      }
      toast.error(err instanceof ApiError ? err.message : 'Failed to update status.');
    }
  }

  async function submitCloseDialog() {
    if (!closeDialog || !data) return;
    if (closeDialog.needRequester && !closeDialog.requesterId) {
      setCloseDialog({ ...closeDialog, error: 'Pick a requester.' });
      return;
    }
    if (closeDialog.needCategory && !closeDialog.categoryId) {
      setCloseDialog({ ...closeDialog, error: 'Pick a category.' });
      return;
    }
    if (closeDialog.needAgent && !closeDialog.agentId) {
      setCloseDialog({ ...closeDialog, error: 'Pick an assigned agent.' });
      return;
    }
    setCloseDialog({ ...closeDialog, busy: true, error: null });
    try {
      let next = data.ticket;
      if (closeDialog.needRequester) {
        const r = await apiFetch<{ ticket: TicketDetail }>(`/api/tickets/${id}/requester`, {
          method: 'PATCH',
          json: { requesterContactId: closeDialog.requesterId },
        });
        next = r.ticket;
      }
      if (closeDialog.needCategory) {
        const r = await apiFetch<{ ticket: TicketDetail }>(`/api/tickets/${id}/category`, {
          method: 'PATCH',
          json: { categoryId: closeDialog.categoryId },
        });
        next = r.ticket;
      }
      if (closeDialog.needAgent) {
        const r = await apiFetch<{ ticket: TicketDetail }>(`/api/tickets/${id}/assignment`, {
          method: 'PATCH',
          json: { assignedAgentId: closeDialog.agentId },
        });
        next = r.ticket;
      }
      const r = await apiFetch<{ ticket: TicketDetail }>(`/api/tickets/${id}/status`, {
        method: 'PATCH',
        json: { status: closeDialog.targetStatus },
      });
      next = r.ticket;
      setData({ ...data, ticket: next });
      setCloseDialog(null);
      toast.success(`Ticket marked ${closeDialog.targetStatus}.`);
    } catch (err) {
      setCloseDialog((cur) =>
        cur
          ? {
              ...cur,
              busy: false,
              error: err instanceof ApiError ? err.message : 'Could not close the ticket.',
            }
          : cur,
      );
    }
  }

  async function changeCategory(categoryId: string) {
    if (!data) return;
    try {
      const res = await apiFetch<{ ticket: TicketDetail }>(`/api/tickets/${id}/category`, {
        method: 'PATCH',
        json: { categoryId: categoryId || null },
      });
      setData({ ...data, ticket: res.ticket });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to update category.');
    }
  }

  async function changeAssignment(agentId: string) {
    if (!data) return;
    try {
      const res = await apiFetch<{ ticket: TicketDetail }>(`/api/tickets/${id}/assignment`, {
        method: 'PATCH',
        json: { assignedAgentId: agentId || null },
      });
      setData({ ...data, ticket: res.ticket });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to update assignment.');
    }
  }

  async function changeType(newType: 'chat' | 'escalation') {
    if (!data) return;
    try {
      const res = await apiFetch<{ ticket: TicketDetail }>(`/api/tickets/${id}/type`, {
        method: 'PATCH',
        json: { type: newType },
      });
      setData({ ...data, ticket: res.ticket });
      // Reload so the new system message ("changed type…") appears in the thread.
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to change ticket type.');
    }
  }

  async function saveClientNumber() {
    if (!data) return;
    setSavingClientNumber(true);
    try {
      const res = await apiFetch<{ ticket: TicketDetail }>(`/api/tickets/${id}/client-number`, {
        method: 'PATCH',
        json: { clientTicketNumber: clientNumberDraft.trim() || null },
      });
      setData({ ...data, ticket: res.ticket });
      setEditingClientNumber(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to update client ticket #.');
    } finally {
      setSavingClientNumber(false);
    }
  }

  async function changeRequester(contactId: string) {
    if (!data) return;
    setRequesterError(null);
    try {
      const res = await apiFetch<{ ticket: TicketDetail }>(`/api/tickets/${id}/requester`, {
        method: 'PATCH',
        json: { requesterContactId: contactId || null },
      });
      setData({ ...data, ticket: res.ticket });
    } catch (err) {
      setRequesterError(err instanceof ApiError ? err.message : 'Failed to update requester.');
    }
  }

  async function changeOrganization(orgId: string) {
    if (!data || !orgId || orgId === data.ticket.organizationId) return;
    const target = organizations.find((o) => o.id === orgId);
    const targetName = target?.name ?? 'the selected organization';
    const blockHourEntries = timeEntries.filter(
      (e) => !e.voidedAt && e.billingBucket === 'block_hours',
    );
    const warning =
      `Move this ticket to ${targetName}?\n\n` +
      `• The requester will be cleared (the current contact belongs to ${data.ticket.organizationName}).\n` +
      (data.ticket.projectId
        ? `• The project link "${data.ticket.projectName ?? ''}" will be cleared.\n`
        : '') +
      (blockHourEntries.length > 0
        ? `• ${blockHourEntries.length} block-hour time ${blockHourEntries.length === 1 ? 'entry' : 'entries'} on this ticket will be re-attributed to ${targetName}.\n`
        : '') +
      `\nThis cannot be undone automatically.`;
    if (!confirm(warning)) return;
    setOrgError(null);
    setOrgChangeBusy(true);
    try {
      const res = await apiFetch<{ ticket: TicketDetail }>(`/api/tickets/${id}/organization`, {
        method: 'PATCH',
        json: { organizationId: orgId },
      });
      setData({ ...data, ticket: res.ticket });
      await load();
    } catch (err) {
      setOrgError(err instanceof ApiError ? err.message : 'Failed to move ticket.');
    } finally {
      setOrgChangeBusy(false);
    }
  }

  async function postReply(e: React.FormEvent) {
    e.preventDefault();
    setReplyError(null);
    setReplyBusy(true);
    try {
      const ccList = replyPublic
        ? replyCc.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean)
        : [];
      if (replyFiles.length > 0) {
        const fd = new FormData();
        fd.set('bodyHtml', reply);
        fd.set('isPublic', String(replyPublic));
        if (ccList.length) fd.set('cc', ccList.join(','));
        for (const f of replyFiles) fd.append('files', f, f.name);
        await apiFetch(`/api/tickets/${id}/messages`, { method: 'POST', body: fd });
      } else {
        await apiFetch(`/api/tickets/${id}/messages`, {
          method: 'POST',
          json: { bodyHtml: reply, isPublic: replyPublic, cc: ccList },
        });
      }
      setReply('');
      setReplyFiles([]);
      await load();
    } catch (err) {
      setReplyError(err instanceof ApiError ? err.message : 'Failed to post reply.');
    } finally {
      setReplyBusy(false);
    }
  }

  // Timer mutations go through the shared hook (which broadcasts to the other
  // windows); `load()` afterwards refreshes this page's time-entry list.
  async function startTimer() {
    setEntryError(null);
    if (await timerCtl.start({ ticketId: id! })) await load();
  }

  async function stopTimer() {
    setEntryError(null);
    if (await timerCtl.stop()) await load();
  }

  async function cancelTimer() {
    if (!confirm('Cancel timer without logging time?')) return;
    setEntryError(null);
    if (await timerCtl.cancel()) await load();
  }

  async function voidEntry(entryId: string) {
    if (!confirm('Void this time entry? It will not count toward billing.')) return;
    try {
      await apiFetch(`/api/tickets/${id}/time/${entryId}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setEntryError(err instanceof ApiError ? err.message : 'Failed to void.');
    }
  }

  async function restoreEntry(entryId: string) {
    try {
      await apiFetch(`/api/timesheets/${entryId}/unvoid`, { method: 'POST' });
      await load();
    } catch (err) {
      setEntryError(err instanceof ApiError ? err.message : 'Failed to restore.');
    }
  }

  async function runAiSuggest() {
    setAiError(null);
    setAiBusy(true);
    try {
      const res = await apiFetch<{ suggestion: TicketSuggestion }>(
        `/api/tickets/${id}/ai-suggest`,
        { method: 'POST' },
      );
      setAiSuggestion(res.suggestion);
      setAiAt(new Date());
    } catch (err) {
      setAiError(err instanceof ApiError ? err.message : 'Failed to get suggestion.');
    } finally {
      setAiBusy(false);
    }
  }

  function useDraftAsReply(text: string) {
    // Plain text from Claude → preserve linebreaks for the rich-text textarea.
    // The reply field is HTML on submit (sanitizeRichHtml on backend), but plain
    // text with newlines round-trips fine through the existing <Textarea>.
    setReply(text);
    setReplyPublic(true);
    // Scroll the reply form into view so the technician sees it landed.
    setTimeout(() => {
      const el = document.querySelector('textarea[name="reply"]') as HTMLTextAreaElement | null;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      }
    }, 50);
  }

  // Sends a conversation (always ending on a user turn) to the backend and
  // appends Claude's reply. Shared by send + retry so a failed turn can be
  // resent without re-typing.
  async function submitAsk(conversation: AskMessage[]) {
    setAskError(null);
    setAskBusy(true);
    try {
      const res = await apiFetch<{ reply: string }>(`/api/tickets/${id}/ai-ask`, {
        method: 'POST',
        json: { conversation },
      });
      setAskMessages([...conversation, { role: 'assistant', content: res.reply }]);
    } catch (err) {
      // Keep the unanswered question in place so the user can retry it.
      setAskMessages(conversation);
      setAskError(err instanceof ApiError ? err.message : 'Failed to reach Claude.');
    } finally {
      setAskBusy(false);
    }
  }

  function sendAsk() {
    const q = askInput.trim();
    if (!q || askBusy) return;
    const conversation: AskMessage[] = [...askMessages, { role: 'user', content: q }];
    setAskMessages(conversation);
    setAskInput('');
    void submitAsk(conversation);
  }

  function retryAsk() {
    // After a failure askMessages ends on the unanswered user turn — resend it.
    if (askBusy || askMessages.length === 0) return;
    if (askMessages[askMessages.length - 1]!.role !== 'user') return;
    void submitAsk(askMessages);
  }

  // Keep the chat pinned to the latest turn as it grows.
  useEffect(() => {
    const el = askScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [askMessages, askBusy]);

  async function loadOrgProjects() {
    if (!data) return;
    try {
      const r = await apiFetch<{ projects: { id: string; name: string; status: string }[] }>(
        `/api/projects?organizationId=${encodeURIComponent(data.ticket.organizationId)}`,
      );
      setOrgProjects(r.projects);
    } catch {
      setOrgProjects([]);
    }
  }

  async function attachToProject() {
    if (!attachProjectId || !data) return;
    setAttachBusy(true);
    setAttachError(null);
    try {
      await apiFetch(`/api/projects/${attachProjectId}/tickets`, {
        method: 'POST',
        json: { ticketId: data.ticket.id },
      });
      setShowAttachProject(false);
      setAttachProjectId('');
      await load();
    } catch (err) {
      setAttachError(err instanceof ApiError ? err.message : 'Failed to attach.');
    } finally {
      setAttachBusy(false);
    }
  }

  // A client filed a simple task as a project. Make it an escalation and take
  // it out of the project; the server deletes the project only if it is the
  // empty placeholder made for this ticket.
  async function notAProject() {
    if (!data) return;
    if (
      !confirm(
        'Change this ticket to an Escalation and take it out of its project? If the project was made just for this ticket and has nothing else in it, the project is deleted.',
      )
    ) {
      return;
    }
    setAttachBusy(true);
    setAttachError(null);
    try {
      const res = await apiFetch<{ projectDeleted: boolean; project: { name: string } | null }>(
        `/api/tickets/${data.ticket.id}/not-a-project`,
        { method: 'POST' },
      );
      toast.success(
        res.projectDeleted
          ? `Now an escalation. The empty project "${res.project?.name}" was deleted.`
          : res.project
            ? `Now an escalation, taken out of "${res.project.name}" (kept: it has other work).`
            : 'Now an escalation.',
      );
      await load();
    } catch (err) {
      setAttachError(err instanceof ApiError ? err.message : 'Could not change the ticket.');
    } finally {
      setAttachBusy(false);
    }
  }

  async function detachFromProject() {
    if (!data?.ticket.projectId) return;
    if (!confirm('Detach this ticket from its project? Time logged on the ticket will stop counting toward project hours.')) return;
    setAttachBusy(true);
    setAttachError(null);
    try {
      await apiFetch(`/api/projects/${data.ticket.projectId}/tickets/${data.ticket.id}`, {
        method: 'DELETE',
      });
      await load();
    } catch (err) {
      setAttachError(err instanceof ApiError ? err.message : 'Failed to detach.');
    } finally {
      setAttachBusy(false);
    }
  }

  async function convertToProject() {
    setConvertError(null);
    setConvertBusy(true);
    try {
      const res = await apiFetch<{ projectTicket: TicketDetail }>(
        `/api/projects/from-quote/${id}`,
        { method: 'POST' },
      );
      navigate(`/tickets/${res.projectTicket.id}`);
    } catch (err) {
      setConvertError(err instanceof ApiError ? err.message : 'Failed to convert.');
    } finally {
      setConvertBusy(false);
    }
  }

  async function openDeleteDialog() {
    setDeletePreviewError(null);
    setDeletePreviewBusy(true);
    try {
      const res = await apiFetch<TicketDeletePreview>(`/api/tickets/${id}/delete-preview`);
      setDeletePreview(res);
    } catch (err) {
      setDeletePreviewError(err instanceof ApiError ? err.message : 'Failed to load preview.');
    } finally {
      setDeletePreviewBusy(false);
    }
  }

  async function confirmDeleteTicket() {
    await apiFetch(`/api/tickets/${id}`, { method: 'DELETE' });
    navigate('/tickets');
  }

  async function convertEscalationToProject() {
    setConvertError(null);
    const budget = Number(escalationBudget);
    if (!Number.isFinite(budget) || budget <= 0) {
      setConvertError('Enter a positive number of budgeted hours.');
      return;
    }
    setConvertBusy(true);
    try {
      const res = await apiFetch<{ projectTicket: TicketDetail }>(
        `/api/projects/from-escalation/${id}`,
        {
          method: 'POST',
          json: {
            budgetedHours: budget,
            name: escalationProjectName.trim() || undefined,
          },
        },
      );
      navigate(`/tickets/${res.projectTicket.id}`);
    } catch (err) {
      setConvertError(err instanceof ApiError ? err.message : 'Failed to convert.');
    } finally {
      setConvertBusy(false);
    }
  }

  if (loadError) return <p className="text-sm text-[var(--color-danger)]">{loadError}</p>;
  if (!data) return <p className="text-[var(--color-muted)]">Loading…</p>;

  const { ticket, messages, timeEntries, totalMinutes } = data;
  const totalHours = (totalMinutes / 60).toFixed(2);
  const timerOnThisTicket = activeTimer && activeTimer.ticketId === ticket.id;
  // Covers a ticketless timer too (ticketId null) — it's running on something
  // else, so offer to switch rather than showing a second Start button.
  const timerOnOtherTicket = activeTimer && activeTimer.ticketId !== ticket.id;
  const canConvert = ticket.type === 'quote' && ticket.status === 'accepted';
  // Quote fields become server-owned once a quote document exists: the
  // document projects them on every save, so the Details panel shows
  // them read-only and points at the document instead.
  const QUOTE_DOCUMENT_FIELDS = ['quoted_hours', 'quoted_amount_usd', 'sow_summary'] as const;
  const canPromoteEscalation =
    ticket.type === 'escalation' &&
    !['solved', 'cancelled', 'dead'].includes(ticket.status) &&
    !ticket.projectId;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/tickets" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Tickets
        </Link>
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <span className="font-mono text-sm text-[var(--color-muted)]">T-{ticket.displayNumber}</span>
          <TypeBadge type={ticket.type} />
          <StatusBadge status={ticket.status}>{ticket.status.replace(/_/g, ' ')}</StatusBadge>
          {ticket.projectId && ticket.projectName && (
            <Link
              to={`/projects/${ticket.projectId}`}
              className="text-xs text-[var(--color-t3-blue)] hover:underline"
            >
              Project: {ticket.projectName}
            </Link>
          )}
        </div>
        {showHuduPasswords && (
          <HuduPasswordModal
            organizationId={ticket.organizationId}
            organizationName={ticket.organizationName}
            onClose={() => setShowHuduPasswords(false)}
          />
        )}
        <h1 className="mt-2 text-3xl font-bold text-[var(--color-header)]">
          {ticketDisplaySubject(ticket.clientTicketNumber, ticket.subject)}
        </h1>
        <p className="mt-1 text-[var(--color-muted)]">
          {ticket.organizationName}
          {ticket.requesterName ? ` · Requester: ${ticket.requesterName}` : ''}
          {ticket.categoryName ? ` · ${ticket.categoryName}` : ''}
        </p>
        <div className="mt-1 flex items-center gap-2 text-sm text-[var(--color-muted)]">
          {editingClientNumber ? (
            <>
              <span className="font-medium text-[var(--color-header)]">Client ticket #</span>
              <input
                type="text"
                value={clientNumberDraft}
                maxLength={50}
                autoFocus
                placeholder="e.g. TS10058"
                onChange={(e) => setClientNumberDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void saveClientNumber();
                  if (e.key === 'Escape') setEditingClientNumber(false);
                }}
                className="w-40 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 py-1 text-sm outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30"
              />
              <Button onClick={() => void saveClientNumber()} disabled={savingClientNumber}>
                {savingClientNumber ? 'Saving…' : 'Save'}
              </Button>
              <button
                type="button"
                onClick={() => setEditingClientNumber(false)}
                className="text-[var(--color-muted)] hover:underline"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <span>
                Client ticket #:{' '}
                {ticket.clientTicketNumber ? (
                  <span className="font-medium text-[var(--color-header)]">{ticket.clientTicketNumber}</span>
                ) : (
                  '—'
                )}
              </span>
              <button
                type="button"
                onClick={() => {
                  setClientNumberDraft(ticket.clientTicketNumber ?? '');
                  setEditingClientNumber(true);
                }}
                className="text-[var(--color-t3-blue)] hover:underline"
              >
                {ticket.clientTicketNumber ? 'Edit' : 'Add'}
              </button>
            </>
          )}
        </div>
      </div>

      {aiSuggestion?.escalateToCall && (
        <div className="rounded-[var(--radius-card)] border-2 border-[var(--color-danger)] bg-red-50 p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <span className="text-2xl leading-none" aria-hidden>
              📞
            </span>
            <div className="flex-1">
              <div className="text-sm font-bold uppercase tracking-wide text-[var(--color-danger)]">
                Call the customer now
              </div>
              {aiSuggestion.escalationReason && (
                <p className="mt-1 text-base font-medium text-[var(--color-header)]">
                  {aiSuggestion.escalationReason}
                </p>
              )}
              <p className="mt-2 text-sm text-[var(--color-body)]">
                Claude detected frustration or repeated back-and-forth on this thread. A written
                reply isn't the right next step — pick up the phone. A two-minute call will
                resolve this faster than another email exchange.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setAiSuggestion(null);
                setAiAt(null);
              }}
              className="text-xs text-[var(--color-muted)] hover:text-[var(--color-header)]"
              title="Dismiss"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {canConvert && (
        <Card className="border-[var(--color-t3-purple)]/30 bg-purple-50/40">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold text-[var(--color-header)]">Quote accepted</h2>
              <p className="mt-1 text-sm text-[var(--color-muted)]">
                Convert this quote into a project. We'll create a project with the quoted hours as
                the budget, plus a kickoff project ticket linked to it.
              </p>
              {convertError && <p className="mt-2 text-sm text-[var(--color-danger)]">{convertError}</p>}
            </div>
            <Button variant="secondary" onClick={() => void convertToProject()} disabled={convertBusy}>
              {convertBusy ? 'Converting…' : 'Convert to project'}
            </Button>
          </div>
        </Card>
      )}

      {!['solved', 'cancelled', 'dead'].includes(ticket.status) && (
        <Card>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-[var(--color-header)]">
                {ticket.projectId && ticket.projectName
                  ? `Attached to project: ${ticket.projectName}`
                  : 'Attach to a project'}
              </h2>
              <p className="mt-1 text-sm text-[var(--color-muted)]">
                {ticket.projectId
                  ? 'Time logged on this ticket counts toward the project. Multiple tickets can attach to one project.'
                  : 'Multiple tickets can roll up under a single project. Pick an existing project for this organization.'}
              </p>
            </div>
            <div className="flex gap-2">
              {/* A project ticket must stay in a project; it can be moved, or
                  turned into an ordinary ticket when it was never a project. */}
              {ticket.type === 'project' && (
                <Button variant="outline" onClick={() => void notAProject()} disabled={attachBusy}>
                  {attachBusy ? 'Working…' : 'Not a project'}
                </Button>
              )}
              {ticket.projectId && ticket.type !== 'project' && (
                <Button
                  variant="outline"
                  onClick={() => void detachFromProject()}
                  disabled={attachBusy}
                >
                  {attachBusy ? 'Working…' : 'Detach'}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => {
                  const next = !showAttachProject;
                  setShowAttachProject(next);
                  if (next && orgProjects === null) void loadOrgProjects();
                }}
              >
                {showAttachProject ? 'Cancel' : ticket.projectId ? 'Move to different project' : 'Attach to project'}
              </Button>
            </div>
          </div>
          {showAttachProject && (
            <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
              <Select
                label="Project"
                value={attachProjectId}
                onChange={(e) => setAttachProjectId(e.target.value)}
              >
                <option value="">— pick one —</option>
                {(orgProjects ?? [])
                  .filter((p) => p.id !== ticket.projectId)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.status !== 'active' ? ` (${p.status})` : ''}
                    </option>
                  ))}
              </Select>
              <Button
                onClick={() => void attachToProject()}
                disabled={attachBusy || !attachProjectId}
              >
                {attachBusy ? 'Attaching…' : 'Attach'}
              </Button>
            </div>
          )}
          {attachError && <p className="mt-2 text-sm text-[var(--color-danger)]">{attachError}</p>}
        </Card>
      )}

      {canPromoteEscalation && (
        <Card className="border-[var(--color-t3-blue)]/30 bg-blue-50/40">
          <h2 className="text-base font-semibold text-[var(--color-header)]">Promote to project</h2>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            This escalation has grown into a project. Time on the new project ticket will continue to
            consume <strong>{ticket.organizationName}</strong>'s block hours. The escalation stays open.
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-[160px_1fr_auto] sm:items-end">
            <FormField
              label="Budget (hours)"
              name="escalation-budget"
              type="number"
              value={escalationBudget}
              onChange={(e) => setEscalationBudget(e.target.value)}
              placeholder="e.g. 12"
            />
            <FormField
              label="Project name (optional)"
              name="escalation-project-name"
              value={escalationProjectName}
              onChange={(e) => setEscalationProjectName(e.target.value)}
              placeholder={ticket.subject}
            />
            <Button
              variant="secondary"
              onClick={() => void convertEscalationToProject()}
              disabled={convertBusy}
            >
              {convertBusy ? 'Promoting…' : 'Promote to project'}
            </Button>
          </div>
          {convertError && <p className="mt-2 text-sm text-[var(--color-danger)]">{convertError}</p>}
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {ticket.type === 'quote' && (
            <Card>
              <h2 className="text-base font-semibold text-[var(--color-header)]">Scope of work</h2>
              <p className="mt-1 mb-4 text-sm text-[var(--color-muted)]">
                What this quote covers. Quotes are written, priced and sent from the Quotes board.
              </p>
              <QuoteScopePanel
                ticketId={ticket.id}
                // `setHasQuoteDocument` is passed by reference, not wrapped
                // in a lambda: the panel keys its load effect off this
                // prop, and a fresh closure each render would refetch in a
                // loop.
                onDocumentPresenceChange={setHasQuoteDocument}
              />
            </Card>
          )}

          {/* The work: progress, the checklist, and the signed scope. */}
          {ticket.type === 'project' && ticket.projectId && (
            <ProjectWorkPanel projectId={ticket.projectId} />
          )}

          <Card className="border-[var(--color-t3-purple)]/30 bg-purple-50/40">
            <h2 className="text-base font-semibold text-[var(--color-header)]">Description</h2>
            <div
              className="mt-2 whitespace-pre-wrap text-sm text-[var(--color-body)]"
              dangerouslySetInnerHTML={{ __html: ticket.descriptionHtml || '<em>No description</em>' }}
            />
            <CustomFieldsPanel
              ticket={ticket}
              onSaved={load}
              lockedKeys={hasQuoteDocument ? QUOTE_DOCUMENT_FIELDS : undefined}
              lockedReason={
                hasQuoteDocument ? 'Set by the quote document.' : undefined
              }
              // What a client is being charged is quote-agent work. Hours
              // are not: they become the project budget the team tracks
              // time against, so they stay on show.
              hiddenKeys={
                ticket.type === 'quote' && !canWriteQuotes(agent?.role)
                  ? ['quoted_amount_usd']
                  : undefined
              }
            />
          </Card>

          <div>
            <h2 className="mb-3 text-base font-semibold text-[var(--color-header)]">Thread</h2>
            <div className="space-y-3">
              {messages.length === 0 ? (
                <Card>
                  <p className="text-center text-[var(--color-muted)]">No replies yet.</p>
                </Card>
              ) : (
                messages.map((m) => {
                  const msgAttachments = data.attachments.filter((a) => a.messageId === m.id);
                  const isCollapsed = collapsedMessages.has(m.id);
                  return (
                    <Card key={m.id} className={m.isPublic ? '' : 'border-amber-300 bg-amber-50/40'}>
                      <button
                        type="button"
                        onClick={() => toggleCollapsed(m.id)}
                        aria-expanded={!isCollapsed}
                        aria-label={isCollapsed ? 'Expand message' : 'Collapse message'}
                        className="-mx-1 mb-2 flex w-[calc(100%+0.5rem)] items-center justify-between gap-2 rounded px-1 py-0.5 text-left text-xs text-[var(--color-muted)] hover:bg-[var(--color-surface)]"
                      >
                        <span className="flex min-w-0 items-center gap-1.5">
                          {isCollapsed ? (
                            <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                          ) : (
                            <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                          )}
                          <span className="min-w-0 truncate">
                            <span className="font-semibold text-[var(--color-header)]">
                              {m.authorName ?? m.authorType}
                            </span>{' '}
                            · {m.authorType}
                            {!m.isPublic && (
                              <span className="ml-2 inline-flex items-center rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-900">
                                Internal
                              </span>
                            )}
                            {isCollapsed && (
                              <span className="ml-2 italic text-[var(--color-muted)]">
                                — {emailPreview(m.bodyHtml)}
                              </span>
                            )}
                          </span>
                        </span>
                        <span className="shrink-0">{new Date(m.createdAt).toLocaleString()}</span>
                      </button>
                      {!isCollapsed && (
                        <>
                          {m.ccEmails.length > 0 && (
                            <p className="mb-2 text-xs text-[var(--color-muted)]">
                              Cc: {m.ccEmails.join(', ')}
                            </p>
                          )}
                          <EmailBody html={m.bodyHtml} />
                          {msgAttachments.length > 0 && (
                            <ul className="mt-3 flex flex-wrap gap-2 border-t border-[var(--color-border)] pt-3 text-xs">
                              {msgAttachments.map((a) => (
                                <li key={a.id}>
                                  <a
                                    href={`/api/tickets/${ticket.id}/attachments/${a.id}`}
                                    className="inline-flex items-center gap-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 py-1 text-[var(--color-t3-blue)] hover:bg-[var(--color-surface)]"
                                    download={a.filename}
                                  >
                                    📎 {a.filename}
                                    <span className="text-[var(--color-muted)]">({Math.ceil(a.sizeBytes / 1024)} KB)</span>
                                  </a>
                                </li>
                              ))}
                            </ul>
                          )}
                          {isAdmin && !m.isPublic && (
                            <div className="mt-3 flex items-center justify-end gap-2 border-t border-amber-200 pt-3">
                              {confirmDeleteMsgId === m.id ? (
                                <>
                                  <span className="text-xs text-[var(--color-muted)]">
                                    Delete this internal note? This can't be undone.
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => void deleteInternalNote(m.id)}
                                    disabled={deletingMsgId === m.id}
                                    className="inline-flex items-center gap-1 rounded-[var(--radius-button)] bg-[var(--color-danger)] px-2.5 py-1 text-xs font-semibold text-white hover:brightness-95 disabled:opacity-60"
                                  >
                                    {deletingMsgId === m.id ? 'Deleting…' : 'Confirm delete'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setConfirmDeleteMsgId(null)}
                                    disabled={deletingMsgId === m.id}
                                    className="inline-flex items-center rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2.5 py-1 text-xs font-medium text-[var(--color-body)] hover:bg-[var(--color-surface)]"
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setConfirmDeleteMsgId(m.id)}
                                  className="inline-flex items-center gap-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2.5 py-1 text-xs font-medium text-[var(--color-danger)] hover:bg-red-50"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                  Delete note
                                </button>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </Card>
                  );
                })
              )}
            </div>
          </div>

          <Card>
            <h2 className="text-base font-semibold text-[var(--color-header)]">Reply</h2>
            <form onSubmit={postReply} className="mt-3 space-y-3">
              <Textarea
                label="Message"
                name="reply"
                rows={4}
                required
                value={reply}
                onChange={(e) => setReply(e.target.value)}
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={replyPublic}
                  onChange={(e) => setReplyPublic(e.target.checked)}
                  className="h-4 w-4 rounded border-[var(--color-border)] text-[var(--color-t3-blue)] focus:ring-[var(--color-t3-blue)]"
                />
                Public reply (visible to client). Uncheck for internal note.
              </label>
              {replyPublic && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-header)]">
                    Cc (optional)
                  </label>
                  <input
                    type="text"
                    value={replyCc}
                    onChange={(e) => setReplyCc(e.target.value)}
                    placeholder="vendor@example.com, manager@client.com"
                    className="mt-1 block w-full rounded-[var(--radius-button)] border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-body)] focus:border-[var(--color-t3-blue)] focus:outline-none focus:ring-1 focus:ring-[var(--color-t3-blue)]"
                  />
                  <p className="mt-1 text-xs text-[var(--color-muted)]">
                    Separate addresses with commas. They're copied on this emailed reply.
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[var(--color-header)]">
                  Attachments (optional, ≤25MB each, ≤10 files)
                </label>
                <input
                  type="file"
                  multiple
                  onChange={(e) => setReplyFiles(Array.from(e.target.files ?? []))}
                  className="mt-1 block w-full text-sm text-[var(--color-body)] file:mr-3 file:rounded-[var(--radius-button)] file:border-0 file:bg-[var(--color-t3-blue)] file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:bg-[var(--color-t3-blue)]/90"
                />
                {replyFiles.length > 0 && (
                  <ul className="mt-2 text-xs text-[var(--color-muted)]">
                    {replyFiles.map((f, i) => (
                      <li key={i}>📎 {f.name} ({Math.ceil(f.size / 1024)} KB)</li>
                    ))}
                  </ul>
                )}
              </div>
              {replyError && <p className="text-sm text-[var(--color-danger)]">{replyError}</p>}
              <Button type="submit" disabled={replyBusy || !reply.trim()}>
                {replyBusy ? 'Posting…' : replyPublic ? 'Post reply' : 'Add internal note'}
              </Button>
            </form>
          </Card>
        </div>

        <div className="space-y-6">
          <TicketHoursCard ticket={ticket} totalMinutes={totalMinutes} />

          <Card>
            <h2 className="text-base font-semibold text-[var(--color-header)]">Ticket details</h2>
            <div className="mt-3 space-y-3">
              <Select
                label="Requester"
                value={ticket.requesterContactId ?? ''}
                onChange={(e) => void changeRequester(e.target.value)}
                hint={
                  contacts.length === 0
                    ? `${ticket.organizationName} has no contacts on file.`
                    : !ticket.requesterContactId
                      ? 'Required to close this ticket (solved, cancelled, or dead).'
                      : undefined
                }
              >
                <option value="">No requester</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name ? `${c.name} (${c.email})` : c.email}
                  </option>
                ))}
              </Select>
              {requesterError && (
                <p className="text-xs text-[var(--color-danger)]">{requesterError}</p>
              )}
              <Select
                label="Organization"
                value={ticket.organizationId}
                onChange={(e) => void changeOrganization(e.target.value)}
                disabled={!isAdmin || orgChangeBusy}
                hint={
                  isAdmin
                    ? orgChangeBusy
                      ? 'Moving ticket…'
                      : 'Changing the organization clears the requester and re-attributes block-hour consumption.'
                    : 'Admins only.'
                }
              >
                {organizations.length === 0 && (
                  <option value={ticket.organizationId}>{ticket.organizationName}</option>
                )}
                {organizations.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                    {o.isActive ? '' : ' (inactive)'}
                  </option>
                ))}
              </Select>
              {orgError && <p className="text-xs text-[var(--color-danger)]">{orgError}</p>}
            </div>
          </Card>

          <Card>
            <h2 className="text-base font-semibold text-[var(--color-header)]">Status & assignment</h2>
            <div className="mt-3 space-y-3">
              {(ticket.type === 'chat' || ticket.type === 'escalation') && (
                <div>
                  <div className="mb-1 block text-sm font-medium text-[var(--color-header)]">Type</div>
                  <div className="flex items-center gap-2">
                    <TypeBadge type={ticket.type} />
                    <Button
                      variant="outline"
                      onClick={() =>
                        void changeType(ticket.type === 'chat' ? 'escalation' : 'chat')
                      }
                    >
                      {ticket.type === 'chat' ? 'Convert to escalation' : 'Convert to chat'}
                    </Button>
                  </div>
                </div>
              )}
              <Select
                label="Status"
                value={ticket.status}
                onChange={(e) => void changeStatus(e.target.value as TicketStatus)}
              >
                {TICKET_STATUSES.filter(
                  (s) => !hiddenStatuses.has(s) || s === ticket.status,
                ).map((s) => (
                  <option key={s} value={s}>
                    {s.replace(/_/g, ' ')}
                  </option>
                ))}
              </Select>
              <Select
                label="Category"
                value={ticket.categoryId ?? ''}
                onChange={(e) => void changeCategory(e.target.value)}
                hint={ticket.categoryId ? undefined : 'Required to close this ticket (solved, cancelled, or dead).'}
              >
                <option value="">No category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
              <Select
                label="Assigned agent"
                value={ticket.assignedAgentId ?? ''}
                onChange={(e) => void changeAssignment(e.target.value)}
                hint={ticket.assignedAgentId ? undefined : 'Required to close this ticket (solved, cancelled, or dead).'}
              >
                <option value="">Unassigned</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
            </div>
            <PartTimeAssigneeBanner
              agent={
                ticket.assignedAgentId
                  ? agents.find((a) => a.id === ticket.assignedAgentId) ?? null
                  : null
              }
            />
          </Card>

          <Card>
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-[var(--color-header)]">
                Claude — suggested next steps
              </h2>
              {aiAt && (
                <span className="text-[10px] uppercase tracking-wide text-[var(--color-muted)]">
                  {aiAt.toLocaleTimeString()}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-[var(--color-muted)]">
              Reads the whole thread and recommends what to do next. Suggestions are advisory —
              always sanity-check before sending.
            </p>

            {!aiSuggestion && !aiBusy && (
              <Button
                onClick={() => void runAiSuggest()}
                disabled={aiBusy}
                className="mt-3 w-full"
                variant="outline"
              >
                Suggest next steps
              </Button>
            )}
            {aiBusy && (
              <p className="mt-3 text-sm text-[var(--color-muted)]">Reading the thread…</p>
            )}
            {aiError && (
              <div className="mt-3 space-y-2">
                <p className="text-sm text-[var(--color-danger)]">{aiError}</p>
                <Button
                  onClick={() => void runAiSuggest()}
                  disabled={aiBusy}
                  variant="outline"
                  className="w-full"
                >
                  Try again
                </Button>
              </div>
            )}

            {aiSuggestion && (
              <div className="mt-3 space-y-3">
                {aiSuggestion.escalateToCall ? (
                  <div className="rounded-[var(--radius-button)] border border-[var(--color-danger)]/40 bg-red-50 px-3 py-2 text-xs text-[var(--color-danger)]">
                    📞 Call the customer — see the banner at the top of this ticket.
                  </div>
                ) : (
                  <div
                    className={`rounded-[var(--radius-button)] border px-3 py-2 text-xs ${
                      aiSuggestion.sentiment === 'frustrated'
                        ? 'border-amber-300 bg-amber-50 text-amber-900'
                        : 'border-[var(--color-border)] bg-white text-[var(--color-muted)]'
                    }`}
                  >
                    Customer sentiment: <span className="font-semibold">{aiSuggestion.sentiment}</span>
                  </div>
                )}

                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-header)]">
                    Next steps
                  </h3>
                  <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-[var(--color-body)]">
                    {aiSuggestion.nextSteps.map((step, i) => (
                      <li key={i}>{step}</li>
                    ))}
                  </ol>
                </div>

                {!aiSuggestion.escalateToCall && aiSuggestion.draftReplyText && (
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-header)]">
                      Draft reply
                    </h3>
                    <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded-[var(--radius-button)] border border-[var(--color-border)] bg-[var(--color-surface)] p-2 font-sans text-sm text-[var(--color-body)]">
                      {aiSuggestion.draftReplyText}
                    </pre>
                    <Button
                      onClick={() => useDraftAsReply(aiSuggestion.draftReplyText!)}
                      variant="outline"
                      className="mt-2 w-full"
                    >
                      Use as my reply
                    </Button>
                  </div>
                )}

                <Button
                  onClick={() => void runAiSuggest()}
                  disabled={aiBusy}
                  variant="outline"
                  className="w-full"
                >
                  {aiBusy ? 'Refreshing…' : 'Regenerate'}
                </Button>
              </div>
            )}
          </Card>

          <Card>
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold text-[var(--color-header)]">Ask Claude</h2>
              {askMessages.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setAskMessages([]);
                    setAskError(null);
                  }}
                  disabled={askBusy}
                  className="text-[10px] uppercase tracking-wide text-[var(--color-muted)] hover:text-[var(--color-header)] disabled:opacity-50"
                >
                  Clear
                </button>
              )}
            </div>
            <p className="mt-1 text-xs text-[var(--color-muted)]">
              Open-ended chat with access to this ticket and every other ticket for{' '}
              {data.ticket.organizationName}. Ask about history, recurring issues, or what to do next.
            </p>

            {askMessages.length > 0 && (
              <div
                ref={askScrollRef}
                className="mt-3 max-h-80 space-y-2 overflow-auto rounded-[var(--radius-button)] border border-[var(--color-border)] bg-[var(--color-surface)] p-2"
              >
                {askMessages.map((m, i) => (
                  <div
                    key={i}
                    className={
                      m.role === 'user'
                        ? 'ml-6 rounded-[var(--radius-button)] bg-[var(--color-t3-blue)]/10 px-3 py-2 text-sm text-[var(--color-body)]'
                        : 'mr-6 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm text-[var(--color-body)]'
                    }
                  >
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-muted)]">
                      {m.role === 'user' ? 'You' : 'Claude'}
                    </div>
                    <div className="whitespace-pre-wrap">{m.content}</div>
                  </div>
                ))}
                {askBusy && (
                  <p className="mr-6 px-3 py-2 text-sm text-[var(--color-muted)]">Claude is thinking…</p>
                )}
              </div>
            )}

            {askError && (
              <div className="mt-3 space-y-2">
                <p className="text-sm text-[var(--color-danger)]">{askError}</p>
                {askMessages.length > 0 &&
                  askMessages[askMessages.length - 1]?.role === 'user' && (
                    <Button
                      onClick={retryAsk}
                      disabled={askBusy}
                      variant="outline"
                      className="w-full"
                    >
                      Try again
                    </Button>
                  )}
              </div>
            )}

            <div className="mt-3 space-y-2">
              <Textarea
                label="Your question"
                name="askClaude"
                value={askInput}
                onChange={(e) => setAskInput(e.target.value)}
                onKeyDown={(e) => {
                  // Enter sends; Shift+Enter inserts a newline.
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendAsk();
                  }
                }}
                rows={2}
                placeholder={
                  askMessages.length === 0
                    ? 'e.g. Has this customer reported this issue before?'
                    : 'Ask a follow-up…'
                }
                disabled={askBusy}
              />
              <Button
                onClick={sendAsk}
                disabled={askBusy || askInput.trim().length === 0}
                variant="outline"
                className="w-full"
              >
                {askBusy ? 'Asking…' : 'Ask'}
              </Button>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-[var(--color-header)]">Time tracking</h2>
              <span
                className="text-sm font-semibold text-[var(--color-header)]"
                title="Billable hours only (block hours, project, quote). Non-billable entries are listed below but don't bill the client."
              >
                {totalHours}h <span className="font-normal text-[var(--color-muted)]">billable</span>
              </span>
            </div>

            {timerOnThisTicket ? (
              <div className="mt-3 space-y-3">
                <div className="rounded-[var(--radius-button)] border border-[var(--color-t3-blue)] bg-[var(--color-t3-blue)]/10 p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-[var(--color-t3-blue)]">
                    Timer running
                  </div>
                  <div className="mt-1 font-mono text-2xl font-bold text-[var(--color-header)]">
                    <LiveElapsed startedAt={activeTimer.startedAt} />
                  </div>
                </div>
                {timerError && <p className="text-sm text-[var(--color-danger)]">{timerError}</p>}
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => void stopTimer()} disabled={timerBusy} className="flex-1">
                    {timerBusy ? 'Stopping…' : 'Stop & log'}
                  </Button>
                  <Button variant="outline" onClick={() => void cancelTimer()} disabled={timerBusy}>
                    Cancel
                  </Button>
                  {isTimeAdmin && (
                    <Button variant="outline" onClick={() => setTimeDialog({ entry: null })}>
                      Add entry
                    </Button>
                  )}
                </div>
              </div>
            ) : timerOnOtherTicket ? (
              <div className="mt-3 space-y-3">
                <div className="rounded-[var(--radius-button)] border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                  Timer is running on{' '}
                  {activeTimer!.ticketId ? (
                    <Link
                      to={`/tickets/${activeTimer!.ticketId}`}
                      className="font-semibold underline"
                    >
                      T-{activeTimer!.ticketDisplayNumber}
                    </Link>
                  ) : (
                    <span className="font-semibold">
                      {activeTimer!.organizationName ?? 'internal time'} (no ticket)
                    </span>
                  )}
                  . Starting a new timer here will commit that one.
                </div>
                {timerError && <p className="text-sm text-[var(--color-danger)]">{timerError}</p>}
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => void startTimer()} disabled={timerBusy}>
                    {timerBusy ? 'Switching…' : 'Switch timer to this ticket'}
                  </Button>
                  {isTimeAdmin && (
                    <Button variant="outline" onClick={() => setTimeDialog({ entry: null })}>
                      Add entry
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-3 space-y-3">
                <p className="text-xs text-[var(--color-muted)]">
                  Will bucket as {ticket.type === 'chat' ? 'non-billable (chat)' : ticket.type === 'escalation' ? 'block hours' : ticket.type}.
                  Notes auto-fill as <span className="font-mono">{ticket.organizationName} - {ticket.subject} - T-{ticket.displayNumber}</span>.
                </p>
                {timerError && <p className="text-sm text-[var(--color-danger)]">{timerError}</p>}
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => void startTimer()} disabled={timerBusy}>
                    {timerBusy ? 'Starting…' : 'Start timer'}
                  </Button>
                  {isTimeAdmin && (
                    <Button variant="outline" onClick={() => setTimeDialog({ entry: null })}>
                      Add entry
                    </Button>
                  )}
                </div>
              </div>
            )}

            {timeEntries.length > 0 && (
              <ul className="mt-4 space-y-2 text-sm">
                {timeEntries.map((e) => (
                  <li
                    key={e.id}
                    className={`flex items-center justify-between rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white p-3 ${e.voidedAt ? 'opacity-50 line-through' : ''}`}
                  >
                    <div>
                      <div className="font-medium">
                        {(e.durationMinutes / 60).toFixed(2)}h · {e.billingBucket.replace('_', ' ')}
                      </div>
                      <div className="text-xs text-[var(--color-muted)]">
                        {e.agentName ?? '—'} · {new Date(e.startedAt).toLocaleString()}
                      </div>
                      {e.notes && <div className="mt-1 text-xs text-[var(--color-body)]">{e.notes}</div>}
                    </div>
                    <div className="flex items-center gap-3">
                      {isTimeAdmin && !e.voidedAt && (
                        <button
                          type="button"
                          onClick={() => setTimeDialog({ entry: e })}
                          className="text-xs text-[var(--color-t3-blue)] hover:underline"
                        >
                          Edit
                        </button>
                      )}
                      {isTimeAdmin && e.voidedAt && (
                        <button
                          type="button"
                          onClick={() => void restoreEntry(e.id)}
                          className="text-xs text-[var(--color-t3-blue)] hover:underline"
                        >
                          Restore
                        </button>
                      )}
                      {!e.voidedAt && (
                        <button
                          type="button"
                          onClick={() => void voidEntry(e.id)}
                          className="text-xs text-[var(--color-danger)] hover:underline"
                        >
                          Void
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {(ticket.huduItglueUrl || ticket.huduCompanyId != null) && (
            <Card>
              <h2 className="text-base font-semibold text-[var(--color-header)]">
                Client passwords
              </h2>
              {ticket.huduItglueUrl ? (
                <>
                  <p className="mt-2 text-sm text-[var(--color-body)]">
                    {ticket.organizationName} is managed in <strong>IT Glue</strong>. The shared T3
                    IT Glue Connect record in Hudu has the launcher URL, username, password, and
                    OTP.
                  </p>
                  <a
                    href={ticket.huduItglueUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-[var(--radius-button)] bg-[var(--color-t3-purple)] px-4 py-3 text-base font-semibold text-white hover:bg-[var(--color-t3-purple)]/90"
                    title="Shared T3 IT Glue Connect record in Hudu"
                  >
                    🔑 Open IT Glue Connect launcher →
                  </a>
                </>
              ) : (
                <>
                  <p className="mt-2 text-sm text-[var(--color-body)]">
                    {ticket.huduMagicLink
                      ? `${ticket.organizationName} is managed via Hudu Bridge. Look up passwords for this client, or jump to their Hudu record.`
                      : `Look up ${ticket.organizationName}'s passwords from Hudu.`}
                  </p>
                  <div className="mt-4 space-y-2">
                    <button
                      type="button"
                      onClick={() => setShowHuduPasswords(true)}
                      className="flex w-full items-center justify-center gap-2 rounded-[var(--radius-button)] bg-[var(--color-t3-blue)] px-4 py-3 text-base font-semibold text-white hover:bg-[var(--color-t3-blue)]/90"
                    >
                      🔑 Look up Hudu passwords
                    </button>
                    {ticket.huduMagicLink && (
                      <a
                        href={ticket.huduMagicLink}
                        target="_blank"
                        rel="noreferrer"
                        className="flex w-full items-center justify-center gap-2 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-4 py-3 text-sm font-medium text-[var(--color-header)] hover:bg-[var(--color-surface)]"
                        title={`Open ${ticket.organizationName} in Hudu`}
                      >
                        Open {ticket.organizationName} in Hudu →
                      </a>
                    )}
                  </div>
                </>
              )}
            </Card>
          )}

          {timeDialog && isTimeAdmin && (
            <TimeEntryEditDialog
              mode={timeDialog.entry ? 'edit' : 'add'}
              isAdmin={isTimeAdmin}
              meId={agent!.id}
              pinnedTicket={
                timeDialog.entry
                  ? null
                  : {
                      id: ticket.id,
                      displayNumber: ticket.displayNumber,
                      subject: ticket.subject,
                    }
              }
              initial={timeDialog.entry ?? undefined}
              onSaved={load}
              onClose={() => setTimeDialog(null)}
            />
          )}

          {isAdmin && (
            <Card className="border-[var(--color-danger)]/40">
              <h2 className="text-base font-semibold text-[var(--color-danger)]">Danger zone</h2>
              <p className="mt-1 text-xs text-[var(--color-muted)]">
                Deleting a ticket permanently removes its messages, time entries, attachments, and
                CSAT survey. Time logged here will stop counting toward any project budget. This
                cannot be undone.
              </p>
              {deletePreviewError && (
                <p className="mt-2 text-sm text-[var(--color-danger)]">{deletePreviewError}</p>
              )}
              <Button
                variant="danger"
                className="mt-3 w-full"
                onClick={() => void openDeleteDialog()}
                disabled={deletePreviewBusy}
              >
                {deletePreviewBusy ? 'Loading…' : 'Delete ticket'}
              </Button>
            </Card>
          )}
        </div>
      </div>

      {deletePreview && (
        <DeleteCascadeDialog
          title={`Delete ticket T-${deletePreview.ticket.displayNumber}?`}
          intro={`This permanently removes "${deletePreview.ticket.subject}" and everything attached to it from ${deletePreview.ticket.organizationName}.`}
          willBeRemoved={[
            { label: 'Messages in the thread', count: deletePreview.counts.messages },
            {
              label: 'Time entries',
              count: deletePreview.counts.timeEntries,
              sublabel:
                deletePreview.counts.timeEntries > 0
                  ? `${(deletePreview.counts.totalMinutes / 60).toFixed(2)}h logged — block-hour consumption records also removed`
                  : undefined,
            },
            { label: 'Attachments', count: deletePreview.counts.attachments },
            { label: 'CSAT surveys', count: deletePreview.counts.csatSurveys },
          ]}
          warnings={
            deletePreview.quoteSourceForProject
              ? [
                  `This ticket is the source quote for project "${deletePreview.quoteSourceForProject}". The project will stay, but its link back to this ticket will be cleared.`,
                ]
              : undefined
          }
          confirmButtonLabel="Delete ticket"
          onConfirm={confirmDeleteTicket}
          onClose={() => setDeletePreview(null)}
        />
      )}

      {closeDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-lg rounded-[var(--radius-card)] bg-white shadow-xl">
            <div className="border-b border-[var(--color-border)] px-6 py-4">
              <h2 className="text-lg font-bold text-[var(--color-header)]">
                Mark this ticket {closeDialog.targetStatus}
              </h2>
              <p className="mt-1 text-sm text-[var(--color-muted)]">
                A few fields are required before closing. Fill them in here and we'll
                set everything in one shot.
              </p>
            </div>
            <div className="space-y-4 px-6 py-5">
              {closeDialog.needRequester && (
                <Select
                  label="Requester"
                  value={closeDialog.requesterId}
                  onChange={(e) =>
                    setCloseDialog((cur) => (cur ? { ...cur, requesterId: e.target.value } : cur))
                  }
                  disabled={closeDialog.busy}
                  hint={
                    contacts.length === 0
                      ? `${ticket.organizationName} has no contacts on file — add one before closing.`
                      : 'Who originally asked for this.'
                  }
                >
                  <option value="">Pick a requester…</option>
                  {contacts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name ? `${c.name} (${c.email})` : c.email}
                    </option>
                  ))}
                </Select>
              )}
              {closeDialog.needCategory && (
                <Select
                  label="Category"
                  value={closeDialog.categoryId}
                  onChange={(e) =>
                    setCloseDialog((cur) => (cur ? { ...cur, categoryId: e.target.value } : cur))
                  }
                  disabled={closeDialog.busy}
                  hint="Drives reporting and retention."
                >
                  <option value="">Pick a category…</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              )}
              {closeDialog.needAgent && (
                <Select
                  label="Assigned agent"
                  value={closeDialog.agentId}
                  onChange={(e) =>
                    setCloseDialog((cur) => (cur ? { ...cur, agentId: e.target.value } : cur))
                  }
                  disabled={closeDialog.busy}
                  hint="Whoever owned the resolution."
                >
                  <option value="">Pick an agent…</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              )}
              {closeDialog.error && (
                <p className="text-sm text-[var(--color-danger)]" role="alert">
                  {closeDialog.error}
                </p>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-[var(--color-border)] px-6 py-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCloseDialog(null)}
                disabled={closeDialog.busy}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => void submitCloseDialog()}
                disabled={closeDialog.busy}
              >
                {closeDialog.busy ? 'Closing…' : `Mark ${closeDialog.targetStatus}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// WhatsApp click-to-chat URLs accept only digits — strip everything else.
// Returns null when there aren't enough digits to dial.
function whatsappHref(phone: string | null): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 7 ? `https://wa.me/${digits}` : null;
}

function PartTimeAssigneeBanner({ agent }: { agent: AgentSummary | null }): React.ReactElement | null {
  if (!agent) return null;
  if (agent.isFullTime) return null;
  const wa = whatsappHref(agent.phoneNumber);
  return (
    <div className="mt-3 flex flex-wrap items-start gap-3 rounded-[var(--radius-card)] border border-amber-300 bg-amber-50/70 px-4 py-3 text-sm">
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-amber-900">
          {agent.name} is part-time — give them a heads-up.
        </p>
        <p className="mt-0.5 text-xs text-amber-800">
          {agent.phoneNumber
            ? `Text them on WhatsApp at ${agent.phoneNumber} so they know this ticket is theirs.`
            : 'No phone number on file — add one in admin → Technicians so dispatchers can WhatsApp them.'}
        </p>
      </div>
      {wa && (
        <a
          href={wa}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-[var(--radius-button)] bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
        >
          <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
        </a>
      )}
    </div>
  );
}
